/**
 * docTemplates.service.ts — Layer 3: Document Template Domain
 */

import { getRequest, postRequest, putRequest, patchRequest, deleteRequest, postFormData } from './http.service';
import {
  URL_DOC_TEMPLATE_BUILDER_BY_ORG,
  URL_DOC_TEMPLATE_BUILDER,
  URL_DOC_TEMPLATE_BUILDER_ANALYZE_UPLOAD,
  URL_DOC_TEMPLATE_BUILDER_MENTIONABLE,
  URL_SKILLS_MENTIONABLE,
  URL_DOC_TEMPLATE_BY_ID,
  URL_DOC_TEMPLATE_CLONE,
} from './url.service';

export interface FileNoteTemplateMentionCandidate {
  id: string;
  name: string;
}

export interface SkillMentionCandidate {
  id: string;
  name: string;
}

export interface SkillLibraryEntry {
  id: string;
  name: string;
  rawMarkdown: string;
  updatedAt: string;
}

/** Fetch all document templates visible to an organisation */
export const fetchDocTemplates = (orgId: string) =>
  getRequest(URL_DOC_TEMPLATE_BUILDER_BY_ORG(orgId));

/** Org's file-note templates (id + name only), for the chat ~template picker */
export const fetchFileNoteTemplatesForMentions = (): Promise<FileNoteTemplateMentionCandidate[]> =>
  getRequest(URL_DOC_TEMPLATE_BUILDER_MENTIONABLE);

/** Enabled organisation/global skills for the chat slash-command picker. */
export const fetchSkillsForMentions = (): Promise<SkillMentionCandidate[]> =>
  getRequest(URL_SKILLS_MENTIONABLE);

/** Fetch all document templates, unfiltered (caller filters by org client-side) */
export const fetchAllDocTemplates = () => getRequest(URL_DOC_TEMPLATE_BUILDER);

/**
 * Skills with enough detail to render a real card (name, description,
 * updated date) — unlike fetchSkillsForMentions, which only returns
 * {id, name} for the lightweight chat picker. Reuses the same
 * doc-template-builder endpoint the admin Skills tab and select-template
 * fields already call; no new backend endpoint.
 */
export const fetchSkillLibraryEntries = async (): Promise<SkillLibraryEntry[]> => {
  const res: any = await fetchAllDocTemplates();
  const list = Array.isArray(res) ? res : res?.data || [];
  return list
    .filter((t: any) => t.templateType === "skill")
    .map((t: any) => ({
      id: t.templateId || t._id,
      name: t.templateName || "Untitled skill",
      rawMarkdown: t.rawMarkdown || "",
      updatedAt: t.updatedAt || "",
    }));
};

/** Create a new document template */
export const createDocTemplate = (payload: Record<string, unknown>) =>
  postRequest(URL_DOC_TEMPLATE_BUILDER, payload);

/** Update an existing document template */
export const updateDocTemplate = (
  templateId: string,
  payload: Record<string, unknown>,
) => putRequest(URL_DOC_TEMPLATE_BY_ID(templateId), payload);

/** Delete a document template */
export const deleteDocTemplate = (templateId: string) =>
  deleteRequest(URL_DOC_TEMPLATE_BY_ID(templateId));

/** Clone a document template */
export const cloneDocTemplate = (payload: Record<string, unknown>) =>
  postRequest(URL_DOC_TEMPLATE_CLONE, payload);

/** Toggle a document template's global (seed) flag */
export const toggleDocTemplateGlobal = (templateId: string) =>
  patchRequest(URL_DOC_TEMPLATE_BY_ID(templateId));

/** Analyze an uploaded document and extract template sections */
export const analyzeUploadedTemplate = (formData: FormData) =>
  postFormData(URL_DOC_TEMPLATE_BUILDER_ANALYZE_UPLOAD, formData);
